import type { ReactNode } from "react";

/**
 * What just happened, in plain words, with a way back beside it.
 *
 * WHY IT IS NOT A TOAST. A toast slides away after three seconds, which means
 * the way back is gone before somebody on a tablet has finished reading what
 * they did. This stays until it is dismissed or the next thing replaces it.
 *
 * WHY THE ACTION'S LABEL IS THE CALLER'S, AND WHY THAT MATTERS MORE THAN IT
 * LOOKS. Of the writes a surveyor makes, exactly one is truly reversible — the
 * design conditions, where every field is in the request and the old values go
 * back exactly. Setting a wall's other side is NOT: re-sending the old answer
 * restores the figure, but the record still says a surveyor confirmed it, at a
 * time, and openings that differed from their wall before are not restored.
 * Confirming a U-value cannot be undone at all; `u_value_confirmed` is one-way
 * through the API, deliberately.
 *
 * So this component refuses to supply the word "Undo". A caller that can truly
 * reverse its write says "Undo"; a caller that can only write the old value
 * back says "Change it back" and passes a `caveat` saying what stays. In a tool
 * whose output is a compliance document, a button that says Undo over a record
 * that remains is a lie worth more than the convenience.
 */

export function UndoNotice({
  what,
  actionLabel,
  caveat,
  onAction,
  onDismiss,
  busy = false,
}: {
  /** What just happened, in the words a surveyor would use. */
  readonly what: string;
  /** "Undo" only where the write is truly reversible. See the header. */
  readonly actionLabel: string;
  /** What stays behind even after the action, when something does. */
  readonly caveat?: string;
  readonly onAction: () => void;
  readonly onDismiss: () => void;
  readonly busy?: boolean;
}): ReactNode {
  return (
    <div className="undo-notice" role="status" aria-live="polite" data-testid="undo-notice">
      <div className="undo-notice__words">
        <p className="undo-notice__what">{what}</p>
        {caveat ? <p className="undo-notice__caveat">{caveat}</p> : null}
      </div>
      <div className="undo-notice__actions">
        <button type="button" className="secondary" disabled={busy} onClick={onAction}>
          {busy ? "Changing…" : actionLabel}
        </button>
        <button
          type="button"
          className="undo-notice__dismiss"
          aria-label="Dismiss this message"
          onClick={onDismiss}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
